import axios from "axios";
import { handleAuthError, getAuthHeaders } from "../../../utils/authUtils";
import { API_BASE_URL } from "../../../environment";

export interface ContractType {
  contract_type_id: number;
  contract_type_display_name: string;
  contract_type_display_key: string;
  contract_type_order: number;
  tenant_id: number;
}

export interface Team {
  team_id: number;
  team_name: string;
}

const getContractUrl = (tenantId: number) => {
  if (!tenantId) throw new Error("Tenant ID is required.");
  return `${API_BASE_URL}/contract/?tenant_id=${tenantId}`;
};

const addTenantToContract = <T extends { tenant_id?: number | null }>(
  contract: Partial<T>,
  tenantId: number
): Partial<T> => {
  if (!tenantId) {
    throw new Error("Tenant ID is required.");
  }

  const authResponse = localStorage.getItem("authResponse");
  let currentUserId = null;

  if (authResponse) {
    try {
      const auth = JSON.parse(authResponse);
      currentUserId = auth.user_id;
    } catch (e) {
      console.warn("Could not parse auth response for user_id");
    }
  }

  return {
    ...contract,
    tenant_id: tenantId,
    created_by: currentUserId,
    updated_by: currentUserId,
  };
};

export interface Contract {
  contract_id?: number;
  crm_entity_id?: number;
  subject: string;
  created_datetime: string;
  updated_datetime: string;
  due_datetime?: string;
  expiration_datetime?: string;
  product_n_services?: string | null;
  amount?: number;
  grand_total?: number;
  currency_code: string;
  description?: string;
  created_by?: {
    user_id: number;
    full_name: string;
    avatar_url?: string;
  };
  updated_by?: {
    user_id: number;
    full_name: string;
    avatar_url?: string;
  };
  tenant_id?: number;
  tags?: {
    insert: { entity_tag_id: number; entity_tag_name: string }[];
  } | null;
  company?: {
    company_id: number;
    company_name: string;
    avatar_url?: string;
  };
  assigned_user?: {
    user_id: number;
    full_name: string;
    avatar_url?: string;
  };
  assigned_team?: {
    team_id: number;
    team_name: string;
  };
  proposal?: {
    proposal_id: number;
    subject: string;
  };
  contract_type?: ContractType;
  assigned_user_id?: number;
  assigned_team_id?: number;
  company_id?: number;
  proposal_id?: number;
  contract_type_id?: number;
}

export interface ContractRequest {
  subject: string;
  due_datetime?: string;
  expiration_datetime?: string;
  product_n_services?: string;
  amount?: number;
  grand_total?: number;
  currency_code: string;
  description?: string;
  source?: string;
  tags?: {
    insert: string[];
  };
  assigned_user_id?: number;
  assigned_team_id?: number;
  company_id?: number;
  primary_contact?: number;
  proposal_id?: number;
  contract_type_id?: number;
  tenant_id?: number;
  created_by?: number;
  updated_by?: number;
  attachment_url?: File[];
}

export class ContractService {
  static async getContracts(
    tenantId: number,
    page: number = 1,
    limit: number = 12,
    filters: Record<string, any> = {}
  ): Promise<{ results: Contract[]; count: number }> {
    try {
      const authHeaders = getAuthHeaders();
      let url = `${API_BASE_URL}/contract/?tenant_id=${tenantId}&page=${page}&limit=${limit}`;

      if (filters.hasOwnProperty('is_archive')) {
        url += `&is_archive=${filters.is_archive}`;
      } else {
        url += `&is_archive=false`;
      }

      if (filters) {
        Object.keys(filters).forEach((key) => {
          if (["searchTerm", "dateRange", "statusFilter", "archivedFilter", "is_archive", "sortBy", "sortOrder", "industryFilter", "countryFilter"].includes(key)) {
            return;
          }
          if (filters[key] !== undefined && filters[key] !== null && filters[key] !== "") {
            url += `&${key}=${encodeURIComponent(filters[key])}`;
          }
        });

        if (filters.searchTerm) {
          url += `&subject=${encodeURIComponent(filters.searchTerm)}`;
        }

        if (filters.dateRange && filters.dateRange[0]) {
          url += `&created_datetime_gte=${filters.dateRange[0].toISOString().split("T")[0]}`;
        }

        if (filters.dateRange && filters.dateRange[1]) {
          url += `&created_datetime_lte=${filters.dateRange[1].toISOString().split("T")[0]}`;
        }

        if (filters.statusFilter) {
          url += `&contract_type_id=${encodeURIComponent(filters.statusFilter)}`;
        }
      }

      const authRaw = localStorage.getItem("authResponse");
      if (authRaw) {
        try {
          const auth = JSON.parse(authRaw);

          const tenantObj = Array.isArray(auth.tenants)
            ? auth.tenants.find(
              (t: any) => String(t.tenant_id) === String(tenantId)
            )
            : null;

          if (tenantObj?.role_key === "SALESUSER" && auth.user_id) {
            url += `&sales_user_id=${auth.user_id}`;
          }
        } catch (error) {
          console.error("Error parsing auth data:", error);
        }
      }

      const response = await axios.get(url, { headers: authHeaders });
      return response.data;
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async createContract(
    contractData: Partial<ContractRequest>
  ): Promise<Contract> {
    try {
      const authHeaders = getAuthHeaders();
      const authResponse = localStorage.getItem("authResponse");

      if (!authResponse) {
        throw new Error("Authentication required");
      }

      const auth = JSON.parse(authResponse);
      const selectedTenantId = localStorage.getItem("selectedTenantId");

      if (!selectedTenantId) {
        throw new Error("Tenant ID is required");
      }

      const contractWithTenant = addTenantToContract(
        contractData,
        Number(selectedTenantId)
      );

      const { attachment_url, ...contractFields } = contractWithTenant;

      if (
        attachment_url &&
        Array.isArray(attachment_url) &&
        attachment_url.length > 0
      ) {
        const formData = new FormData();

        Object.entries(contractFields).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value.toString());
          }
        });

        attachment_url.forEach((file: File) => {
          formData.append("attachment_url", file);
        });

        const { "Content-Type": _, ...fileUploadHeaders } = authHeaders;

        const response = await axios.post(
          `${API_BASE_URL}/contract/`,
          formData,
          { headers: fileUploadHeaders }
        );

        return response.data;
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/contract/`,
          contractWithTenant,
          { headers: authHeaders }
        );

        return response.data;
      }
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async updateContract(
    contractId: number,
    contractData: Partial<ContractRequest>
  ): Promise<Contract> {
    try {
      const authHeaders = getAuthHeaders();
      const authResponse = localStorage.getItem("authResponse");

      if (!authResponse) {
        throw new Error("Authentication required");
      }

      const auth = JSON.parse(authResponse);
      const selectedTenantId = localStorage.getItem("selectedTenantId");

      if (!selectedTenantId) {
        throw new Error("Tenant ID is required");
      }

      const contractWithTenant = addTenantToContract(
        contractData,
        Number(selectedTenantId)
      );

      const { attachment_url, ...contractFields } = contractWithTenant;

      if (
        attachment_url &&
        Array.isArray(attachment_url) &&
        attachment_url.length > 0
      ) {
        const formData = new FormData();

        Object.entries(contractFields).forEach(([key, value]) => {
          if (value !== undefined && value !== null) {
            formData.append(key, value.toString());
          }
        });

        attachment_url.forEach((file: File) => {
          formData.append("attachment_url", file);
        });

        const { "Content-Type": _, ...fileUploadHeaders } = authHeaders;

        const response = await axios.patch(
          `${API_BASE_URL}/contract/${contractId}/`,
          formData,
          { headers: fileUploadHeaders }
        );

        return response.data;
      } else {
        const response = await axios.patch(
          `${API_BASE_URL}/contract/${contractId}/`,
          contractWithTenant,
          { headers: authHeaders }
        );

        return response.data;
      }
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async deleteContract(
    contractId: number,
    tenantId: number
  ): Promise<void> {
    try {
      const authHeaders = getAuthHeaders();

      await axios.delete(`${API_BASE_URL}/contract/${contractId}/`, {
        headers: authHeaders,
      });
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async getContractById(contractId: number): Promise<Contract> {
    try {
      const authHeaders = getAuthHeaders();

      const response = await axios.get(
        `${API_BASE_URL}/contract/${contractId}/`,
        { headers: authHeaders }
      );

      return response.data;
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async getTeams(tenantId: number): Promise<Team[]> {
    try {
      const authHeaders = getAuthHeaders();

      const response = await axios.get(
        `${API_BASE_URL}/team/?tenant_id=${tenantId}&response_mode=light`,
        { headers: authHeaders }
      );

      return response.data.results || [];
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }

  static async getContractTypes(tenantId: number): Promise<ContractType[]> {
    try {
      const authHeaders = getAuthHeaders();

      const response = await axios.get(
        `${API_BASE_URL}/contract-type/?tenant_id=${tenantId}`,
        { headers: authHeaders }
      );

      return response.data.results || [];
    } catch (error) {
      handleAuthError(error);
      throw error;
    }
  }
}
